import { motion } from "framer-motion";
import { Star, ShoppingBag, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Sparkle } from "@/components/common/Decor";
import { txt } from "@/data/siteText";

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full bg-[#F2E8E6] px-3.5 py-1 text-[0.62rem] uppercase tracking-[0.22em] text-[#232222] font-semibold ${className}`}>
    <Sparkle className="h-2.5 w-2.5 text-[#F6B393]" />{children}
  </span>
);

export const Stars = ({ n = 5, className = "h-4 w-4" }) => (
  <div className="flex">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${className} ${i < n ? "fill-[#F4B999] text-[#F4B999]" : "text-[#F2E8E6]"}`} />
    ))}
  </div>
);

export const Btn = ({ to, href, onClick, children, variant = "primary", className = "", testid, type }) => {
  const base =
    "group inline-flex items-center justify-center gap-2.5 rounded-full px-8 py-4 text-xs font-medium uppercase tracking-[0.14em] transition-all duration-300";
  const styles = {
    primary: "bg-[#FBC4A8] text-[#232222] font-semibold shadow-[0_12px_30px_-12px_rgba(202,166,140,0.6)] hover:bg-[#F6B393] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-14px_rgba(202,166,140,0.65)]",
    accent: "bg-[#FBC4A8] text-[#232222] shadow-[0_12px_30px_-12px_rgba(181,138,76,0.6)] hover:bg-[#FBC4A8] hover:-translate-y-0.5",
    ink: "bg-[#232222] text-[#FFFCFA] shadow-[0_10px_30px_-10px_rgba(62,50,43,0.5)] hover:bg-[#8A7F86] hover:-translate-y-0.5",
    outline: "rounded-full border-2 border-[#E3D7C0] text-[#2C2B2C] hover:border-[#FBC4A8] hover:bg-white",
    ghost: "text-[#2C2B2C] hover:text-[#FBC4A8] px-0 py-0",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls} onClick={onClick}>{children}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <button type={type || "button"} data-testid={testid} onClick={onClick} className={cls}>{children}</button>;
};

export const AddToCartButton = ({ sku, label = "Add to Bag", quantity = 1, variant = "primary", className = "", testid }) => {
  const { addItem } = useCart();
  return (
    <Btn
      variant={variant}
      testid={testid || `add-to-cart-${sku}`}
      className={className}
      onClick={() => {
        addItem(sku, quantity);
        toast.success("Added to your bag", { description: "Free express shipping included." });
      }}
    >
      <ShoppingBag className="h-4 w-4" /> {label}
    </Btn>
  );
};

const slugSlot = (s) => (typeof s === "string" && s.trim())
  ? "sec_" + s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 44)
  : null;

export const SectionHeading = ({ overline, title, sub, align = "left", className = "", slot }) => {
  const base = slot || slugSlot(title);
  const tSlot = base && typeof title === "string" ? base : null;
  const sSlot = base && typeof sub === "string" ? `${base}_sub` : null;
  return (
    <div className={`${align === "center" ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} ${className}`}>
      {overline && <Overline className="mb-4">{overline}</Overline>}
      <h2 className="font-display text-balance text-4xl sm:text-5xl font-normal tracking-tight leading-[1.05] text-[#2C2B2C]"
        {...(tSlot ? { "data-text-slot": tSlot } : {})}>
        {tSlot ? txt(tSlot, title) : title}
      </h2>
      {sub && <p className="mt-5 text-base md:text-lg font-light leading-relaxed text-[#8A7F86]"
        {...(sSlot ? { "data-text-slot": sSlot } : {})}>
        {sSlot ? txt(sSlot, sub) : sub}
      </p>}
    </div>
  );
};

export const Tick = ({ children }) => (
  <li className="flex items-start gap-3">
    <Check className="mt-0.5 h-4 w-4 flex-none text-[#FBC4A8]" strokeWidth={2.5} />
    <span className="text-[#8A7F86]">{children}</span>
  </li>
);
